import { useMemo } from "react";
import { Sparkles, Sliders } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/utils";
import type { FilterConfig } from "../../../api/posterService";

import { FILTER_PRESETS, detectActivePreset, type FilterPresetId } from "./filterPresets";

/**
 * v2.9.2 画面预设面板,顶部 [普通][高级] segmented control 切换。
 * Step6 生成前预设(所有图共享)、详情页编辑器单图微调,都用这套交互。
 *
 * V6 fix4 (2026-05-12): 删 "文字大小" 那套(三档 chip + 字号偏移 slider)。
 * AI 自己画文字,客户端 font_size_offset 已无消费方。
 *
 * UX 原则(过 ui-pro-max 审):
 * - 普通模式陈列 6 个色彩 chip — 小白用户看人话不看数值
 * - 高级模式 4 个 slider(明暗/反差/鲜艳度/冷暖)
 * - 顶部 segmented control 直接切换,不藏在折叠里
 * - 切到高级后修改的值,切回普通会显示 "自定义" hint chip(不破坏用户的工作)
 * - 触摸目标 ≥ 36px,响应式 wrap,移动端友好
 */
export interface AppearancePresetPanelProps {
  filter: FilterConfig;
  mode: "simple" | "advanced";
  onModeChange: (mode: "simple" | "advanced") => void;
  onChange: (next: FilterConfig) => void;
}

export function AppearancePresetPanel({
  filter,
  mode,
  onModeChange,
  onChange,
}: AppearancePresetPanelProps) {
  const { t } = useTranslation("poster");
  const activePreset = useMemo(() => detectActivePreset(filter), [filter]);

  // 高级模式下用户改了值,切回普通 chip 不再匹配任何 preset → 标记为"自定义"
  const isCustom =
    activePreset === "default" &&
    ((filter.brightness ?? 0) !== 0 ||
      (filter.contrast ?? 0) !== 0 ||
      (filter.saturation ?? 0) !== 0 ||
      (filter.warmth ?? 0) !== 0);

  function applyPreset(id: FilterPresetId) {
    const preset = FILTER_PRESETS.find((p) => p.id === id);
    if (!preset) return;
    onChange({ ...preset.params });
  }

  return (
    <div className="space-y-4">
      {/* segmented control */}
      <div className="flex gap-1 rounded-lg border bg-muted/30 p-1">
        <ModeButton
          active={mode === "simple"}
          icon={<Sparkles className="h-3.5 w-3.5" />}
          label={t("editor.presetSimple")}
          hint={t("editor.presetSimpleHint")}
          onClick={() => onModeChange("simple")}
        />
        <ModeButton
          active={mode === "advanced"}
          icon={<Sliders className="h-3.5 w-3.5" />}
          label={t("editor.presetAdvanced")}
          hint={t("editor.presetAdvancedHint")}
          onClick={() => onModeChange("advanced")}
        />
      </div>

      {mode === "simple" ? (
        <SimpleMode activePreset={activePreset} isCustom={isCustom} onApplyPreset={applyPreset} />
      ) : (
        <AdvancedMode filter={filter} onChange={onChange} />
      )}
    </div>
  );
}

function ModeButton({
  active,
  icon,
  label,
  hint,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-1 flex-col items-center gap-0.5 rounded-md px-3 py-2 transition-colors",
        active
          ? "bg-background font-medium text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground"
      )}
    >
      <span className="flex items-center gap-1.5 text-sm">
        {icon}
        {label}
      </span>
      <span className="text-2xs opacity-70">{hint}</span>
    </button>
  );
}

// ─── 普通模式 ────────────────────────────────────────────

function SimpleMode({
  activePreset,
  isCustom,
  onApplyPreset,
}: {
  activePreset: FilterPresetId;
  isCustom: boolean;
  onApplyPreset: (id: FilterPresetId) => void;
}) {
  const { t } = useTranslation("poster");
  return (
    <div className="space-y-4">
      <section>
        <h4 className="mb-2 text-sm font-medium">{t("editor.presetColorTitle")}</h4>
        <div className="flex flex-wrap gap-2">
          {FILTER_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              title={preset.description}
              onClick={() => onApplyPreset(preset.id)}
              className={cn(
                "min-h-[36px] rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                activePreset === preset.id && !isCustom
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border hover:border-primary/50"
              )}
            >
              {preset.label}
            </button>
          ))}
          {isCustom && (
            <span
              className="flex min-h-[36px] items-center rounded-md border border-warning/50 bg-warning/10 px-3 py-1.5 text-sm text-warning"
              title={t("editor.presetCustomTooltip")}
            >
              {t("editor.presetCustom")}
            </span>
          )}
        </div>
        <p className="mt-1.5 text-2xs text-muted-foreground">{t("editor.presetNoStack")}</p>
      </section>

      {/* V6 fix4 (2026-05-12): 文字大小 section 已删 — AI 自己画文字,客户端 offset 无消费方 */}
    </div>
  );
}

// ─── 高级模式 ────────────────────────────────────────────

function AdvancedMode({
  filter,
  onChange,
}: {
  filter: FilterConfig;
  onChange: (next: FilterConfig) => void;
}) {
  const { t } = useTranslation("poster");
  return (
    <div className="space-y-3 rounded-md border bg-muted/10 p-3">
      <SliderRow
        label={t("editor.sliderBrightness")}
        value={filter.brightness ?? 0}
        onChange={(v) => onChange({ ...filter, brightness: v })}
      />
      <SliderRow
        label={t("editor.sliderContrast")}
        value={filter.contrast ?? 0}
        onChange={(v) => onChange({ ...filter, contrast: v })}
      />
      <SliderRow
        label={t("editor.sliderSaturation")}
        value={filter.saturation ?? 0}
        onChange={(v) => onChange({ ...filter, saturation: v })}
      />
      <SliderRow
        label={t("editor.sliderWarmth")}
        value={filter.warmth ?? 0}
        onChange={(v) => onChange({ ...filter, warmth: v })}
      />
      {/* V6 fix4 (2026-05-12): 字号偏移已删 — AI 自己画文字,客户端 offset 没消费方 */}
    </div>
  );
}

function SliderRow({
  label,
  value,
  min = -50,
  max = 50,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
}) {
  const id = `slider-${label}`;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label htmlFor={id} className="text-xs text-muted-foreground">
          {label}
        </label>
        <span className="font-mono text-2xs">{value > 0 ? `+${value}` : value}</span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary"
      />
    </div>
  );
}
